import { PDF_COLORS, PDF_FONTS, PDF_LAYOUT } from "../../utils/pdfStyles";
import { asText } from "../../utils/pdfHelpers";

const cleanLogoUri = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (trimmed === "" || trimmed === "null" || trimmed === "undefined") return null;

  if (trimmed.startsWith("data:image/")) return trimmed;
  if (trimmed.startsWith("iVBORw0KGgo")) return `data:image/png;base64,${trimmed}`;
  if (trimmed.startsWith("/9j/")) return `data:image/jpeg;base64,${trimmed}`;
  if (trimmed.startsWith("PHN2Zy") || trimmed.includes("<svg")) return null; // SVG no soportado nativo en doc.addImage
  return `data:image/png;base64,${trimmed}`;
};

export const drawHeaderEmpresa = (doc, { meta, documentTitle = "PLAN DE PARTO", folio = null, startY = null }) => {
  const x = PDF_LAYOUT.marginX;
  const w = PDF_LAYOUT.contentWidth;
  const y = startY || PDF_LAYOUT.marginTop;

  const empresa = asText(meta?.empresa || meta?.nombreEmpresa || "INSTITUCIÓN PRESTADORA DE SALUD");
  const nit = asText(meta?.nit ? `NIT: ${meta.nit}` : "");
  const sede = asText(meta?.sede || meta?.nombreSede || "");
  const codigoSede = asText(meta?.codigoSede ? `Cód. Habilitación: ${meta.codigoSede}` : "");
  const fecha = asText(meta?.fechaImpresion || new Date().toLocaleString());

  const rawLogo = meta?.logo || meta?.logoEmpresa || null;
  const logoDataUri = cleanLogoUri(rawLogo);

  const logoMaxW = 68;
  const logoMaxH = 44;
  let hasRenderedLogo = false;

  // 1. Render Logo (Columna Izquierda extrema)
  if (logoDataUri) {
    try {
      const format = logoDataUri.includes("image/jpeg") || logoDataUri.includes("image/jpg")
        ? "JPEG"
        : logoDataUri.includes("image/webp")
        ? "WEBP"
        : "PNG";
      doc.addImage(logoDataUri, format, x, y, logoMaxW, logoMaxH, undefined, "FAST");
      hasRenderedLogo = true;
    } catch (e) {
      console.warn("[drawHeaderEmpresa] Error renderizando logo con doc.addImage:", e);
    }
  }

  // Si no hay logo o falló, dibujamos un marco institucional reservado sutil
  if (!hasRenderedLogo) {
    doc.setDrawColor(...PDF_COLORS.borderLight);
    doc.setFillColor(...PDF_COLORS.bgLight);
    doc.roundedRect(x, y, logoMaxW, logoMaxH, 4, 4, "FD");

    doc.setFont(PDF_FONTS.family, "bold");
    doc.setFontSize(8);
    doc.setTextColor(...PDF_COLORS.border);
    doc.text("LOGO IPS", x + logoMaxW / 2, y + logoMaxH / 2 + 3, { align: "center" });
  }

  // 2. Información Institucional (Al lado derecho del logo)
  const infoStartX = x + logoMaxW + 10;
  const maxInfoW = 230; // Delimitador estricto para evitar cruces
  let currentInfoY = y + 10;

  // Razón Social
  doc.setFont(PDF_FONTS.family, "bold");
  doc.setFontSize(10.5);
  doc.setTextColor(...PDF_COLORS.textDark);
  const empresaLines = doc.splitTextToSize(empresa.toUpperCase(), maxInfoW);
  doc.text(empresaLines, infoStartX, currentInfoY);
  currentInfoY += empresaLines.length * 11;

  // NIT
  if (nit) {
    doc.setFont(PDF_FONTS.family, "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...PDF_COLORS.textMuted);
    doc.text(nit, infoStartX, currentInfoY);
    currentInfoY += 10;
  }

  // Sede (con auto-wrap estricto en su columna)
  if (sede) {
    doc.setFont(PDF_FONTS.family, "normal");
    doc.setFontSize(8);
    doc.setTextColor(...PDF_COLORS.textMuted);
    const sedeLines = doc.splitTextToSize(sede, maxInfoW);
    doc.text(sedeLines, infoStartX, currentInfoY);
    currentInfoY += sedeLines.length * 9;
  }

  // Código de Habilitación
  if (codigoSede) {
    doc.setFont(PDF_FONTS.family, "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...PDF_COLORS.textMuted);
    doc.text(codigoSede, infoStartX, currentInfoY);
    currentInfoY += 8;
  }

  // 3. Columna Derecha (Título del Documento, Folio y Fecha Emisión)
  const rightX = x + w;
  let rightY = y + 10;

  doc.setFont(PDF_FONTS.family, "bold");
  doc.setFontSize(PDF_FONTS.sizes.title - 1);
  doc.setTextColor(...PDF_COLORS.primaryDark);
  doc.text(documentTitle.toUpperCase(), rightX, rightY, { align: "right" });
  rightY += 12;

  if (folio) {
    doc.setFont(PDF_FONTS.family, "bold");
    doc.setFontSize(PDF_FONTS.sizes.label + 0.5);
    doc.setTextColor(...PDF_COLORS.primary);
    doc.text(`Folio Registro: #${folio}`, rightX, rightY, { align: "right" });
    rightY += 11;
  }

  doc.setFont(PDF_FONTS.family, "normal");
  doc.setFontSize(PDF_FONTS.sizes.small);
  doc.setTextColor(...PDF_COLORS.textMuted);
  doc.text(`Fecha Emisión: ${fecha}`, rightX, rightY, { align: "right" });
  rightY += 9;

  // 4. Línea Divisoria Inferior Calculada Dinámicamente
  const headerBottomY = Math.max(y + logoMaxH, currentInfoY, rightY) + 6;
  doc.setDrawColor(...PDF_COLORS.border);
  doc.setLineWidth(1);
  doc.line(x, headerBottomY, x + w, headerBottomY);

  return headerBottomY + 8;
};
